import { splitWhere } from './group';
import { frameIndexAt, type Telemetry } from './telemetry';

export type MomentKind = 'brake' | 'launch' | 'lateral' | 'disengage';

export interface Moment {
  readonly kind: MomentKind;
  readonly time: number;
  readonly start: number;
  readonly end: number;
  readonly peak: number;
}

const HARD_BRAKE_MPS2 = 4;
const LAUNCH_MPS2 = 5;
const LATERAL_MPS2 = 5;
const HALF_WINDOW_S = 0.25;
const MAX_FRAME_GAP_S = 0.3;
const BRAKE_CONFIRM_S = 1;
const MIN_BRAKE_SPEED_DROP_MPS = 1.5;
const MERGE_GAP_S = 2;

interface Segment {
  readonly from: number;
  readonly to: number;
}

interface Run {
  readonly first: number;
  readonly last: number;
  readonly peak: number;
}

export function segments(time: Float64Array): readonly Segment[] {
  const bounds: Segment[] = [];
  let from = 0;
  for (let i = 1; i <= time.length; i++) {
    if (i < time.length && time[i]! - time[i - 1]! <= MAX_FRAME_GAP_S) continue;
    if (i > from) bounds.push({ from, to: i });
    from = i;
  }
  return bounds;
}

export function rollingMean(time: Float64Array, values: Float32Array, { from, to }: Segment): Float64Array {
  const means = new Float64Array(to - from);
  let low = from;
  let high = from;
  let sum = 0;
  for (let i = from; i < to; i++) {
    for (; high < to && time[high]! <= time[i]! + HALF_WINDOW_S; high++) sum += values[high]!;
    for (; time[low]! < time[i]! - HALF_WINDOW_S; low++) sum -= values[low]!;
    means[i - from] = sum / (high - low);
  }
  return means;
}

function findRuns(time: Float64Array, offset: number, scores: Float64Array, threshold: number): readonly Run[] {
  const hits: number[] = [];
  scores.forEach((score, i) => {
    if (score >= threshold) hits.push(i);
  });
  return splitWhere(hits, (previous, current) => time[offset + current]! - time[offset + previous]! > MERGE_GAP_S).map((group) => ({
    first: group[0]!,
    last: group.at(-1)!,
    peak: group.reduce((best, i) => (scores[i]! > scores[best]! ? i : best), group[0]!),
  }));
}

function speedAt(telemetry: Telemetry, segment: Segment, time: number): number {
  const times = telemetry.time.subarray(segment.from, segment.to);
  return telemetry.speed[segment.from + Math.max(0, frameIndexAt(times, time))]!;
}

const confirmsBraking = (telemetry: Telemetry, segment: Segment, time: number): boolean =>
  speedAt(telemetry, segment, time - BRAKE_CONFIRM_S) - speedAt(telemetry, segment, time + BRAKE_CONFIRM_S) >= MIN_BRAKE_SPEED_DROP_MPS;

function accelMoments(telemetry: Telemetry, segment: Segment, kind: MomentKind, scores: Float64Array, threshold: number): readonly Moment[] {
  const { time } = telemetry;
  return findRuns(time, segment.from, scores, threshold).map((run) => ({
    kind,
    time: time[segment.from + run.peak]!,
    start: time[segment.from + run.first]!,
    end: time[segment.from + run.last]!,
    peak: scores[run.peak]!,
  }));
}

function disengagements(telemetry: Telemetry, { from, to }: Segment): readonly Moment[] {
  const moments: Moment[] = [];
  for (let i = from + 1; i < to; i++) {
    if (telemetry.autopilot[i - 1]! === 0 || telemetry.autopilot[i]! !== 0) continue;
    const time = telemetry.time[i]!;
    moments.push({ kind: 'disengage', time, start: time, end: time, peak: 0 });
  }
  return moments;
}

function segmentMoments(telemetry: Telemetry, segment: Segment): readonly Moment[] {
  const longitudinal = rollingMean(telemetry.time, telemetry.accelY, segment);
  const lateral = rollingMean(telemetry.time, telemetry.accelX, segment);
  const braking = accelMoments(telemetry, segment, 'brake', longitudinal, HARD_BRAKE_MPS2);
  return [
    ...braking.filter((moment) => confirmsBraking(telemetry, segment, moment.time)),
    ...accelMoments(telemetry, segment, 'launch', longitudinal.map((value) => -value), LAUNCH_MPS2),
    ...accelMoments(telemetry, segment, 'lateral', lateral.map(Math.abs), LATERAL_MPS2),
    ...disengagements(telemetry, segment),
  ];
}

export const detectMoments = (telemetry: Telemetry): readonly Moment[] =>
  segments(telemetry.time)
    .flatMap((segment) => segmentMoments(telemetry, segment))
    .sort((a, b) => a.time - b.time);

export interface ModeChange {
  readonly time: number;
  readonly from: number;
  readonly to: number;
}

export interface PlacedTelemetry {
  readonly start: number;
  readonly telemetry: Telemetry;
}

export function modeChanges(clips: readonly PlacedTelemetry[]): readonly ModeChange[] {
  const changes: ModeChange[] = [];
  let previous: number | undefined;
  for (const { start, telemetry } of [...clips].sort((a, b) => a.start - b.start)) {
    telemetry.autopilot.forEach((state, i) => {
      if (previous !== undefined && state !== previous) changes.push({ time: start + telemetry.time[i]! * 1000, from: previous, to: state });
      previous = state;
    });
  }
  return changes;
}
